import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ModalHeaderComponent } from './moda-header/modal-header.component';

export interface ModalConfig {
  title?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  height?: string;
  maxHeight?: string;
  minHeight?: string;
  padding?: string;
}

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, ModalHeaderComponent],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  // Dependencies
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  // Inputs
  isOpen = input<boolean>(false);
  config = input<ModalConfig>({});

  // Outputs
  closed = output<void>();
  backdropClicked = output<void>();

  // View reference
  @ViewChild('modalBackdrop', { static: false })
  modalBackdrop?: ElementRef<HTMLElement>;

  // Computed values
  modalConfig = computed(() => {
    const defaultConfig: ModalConfig = {
      showCloseButton: true,
      closeOnBackdropClick: true,
      closeOnEscape: true,
      width: 'auto',
      minWidth: '30rem',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      minHeight: '30rem',
      padding: '1rem 1.25rem 0 1.25rem',
    };
    return { ...defaultConfig, ...this.config() };
  });

  modalStyles = computed(() => {
    const cfg = this.modalConfig();
    return {
      width: cfg.width,
      maxWidth: cfg.maxWidth,
      height: cfg.height,
      maxHeight: cfg.maxHeight,
      padding: cfg.padding,
      minWidth: cfg.minWidth,
      minHeight: cfg.minHeight,
    };
  });

  // Effects
  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    });
  }

  ngAfterViewInit(): void {
    // Move modal to body when view is initialized and modal is open
    if (this.isOpen()) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.moveToBody();
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up: remove from body if still attached
    this.removeFromBody();
    // Restore body overflow
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  // Methods
  close(): void {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    const cfg = this.modalConfig();
    if (
      cfg.closeOnBackdropClick &&
      (event.target as HTMLElement).classList.contains('modal__backdrop')
    ) {
      this.backdropClicked.emit();
      this.close();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const cfg = this.modalConfig();
    if (cfg.closeOnEscape && event.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  private handleOpen(): void {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
      // Use requestAnimationFrame to ensure DOM is ready after change detection
      requestAnimationFrame(() => {
        this.moveToBody();
      });
    }
  }

  private handleClose(): void {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      this.removeFromBody();
    }
  }

  private moveToBody(): void {
    if (typeof document === 'undefined') return;

    const modalElement = this.elementRef.nativeElement;
    const body = document.body;

    // Only move if not already in body
    if (modalElement.parentNode !== body) {
      this.renderer.appendChild(body, modalElement);
    }
  }

  private removeFromBody(): void {
    if (typeof document === 'undefined') return;

    const modalElement = this.elementRef.nativeElement;
    const body = document.body;

    // Remove from body if it's a direct child
    if (modalElement.parentNode === body) {
      this.renderer.removeChild(body, modalElement);
    }
  }
}
